// Utilities for Social Sign-On (SSO) error handling

// Regex for textual cancellation indicators across providers/browsers
export const CANCEL_TEXT_RE =
  /(?:cancel|cancelled|canceled|aborted|user\s*closed|popup(?:_closed_by_user|\s*closed)|dismissed)/i;

// Regex for common cancellation codes across providers/platforms
// Note: 1001 (Apple) handled explicitly in friendlySSOError to show a specific Apple message
// Note: 5 is intentionally NOT included here due to being overly generic; see isSSOCancelError gating
export const CANCEL_CODE_RE =
  /^(?:12501|4201|CANCELED|CANCELLED|ERR_CANCELED|SIGN_IN_CANCELLED|SIGN_IN_CANCELED)$/i;

// Regex for common network-related indicators
export const NETWORK_RE =
  /(?:network|timed\s*out|timeout|offline|address\s*unreachable|ERR_NETWORK)/i;

// Detect if a social sign-in error was caused by user cancellation
export const isSSOCancelError = (err: any): boolean => {
  const msg = (
    err?.message ||
    err?.error ||
    err?.reason ||
    err?.statusText ||
    err?.data?.message ||
    err?.data?.error ||
    ""
  )
    .toString()
    .toLowerCase();

  const codeStr = (
    err?.code ??
    err?.status ??
    err?.errorCode ??
    err?.data?.code ??
    ""
  ).toString();

  // Some SDKs use code '5' for various errors; only treat as cancel if text indicates cancellation
  const isGenericFiveCancel = codeStr === "5" && CANCEL_TEXT_RE.test(msg);

  return (
    CANCEL_TEXT_RE.test(msg) ||
    CANCEL_CODE_RE.test(codeStr) ||
    isGenericFiveCancel
  );
};

// Map raw SSO errors to friendly UI messages
export const friendlySSOError = (err: any, provider: string): string => {
  const msg = (err?.message || "").toString();
  const codeStr = (
    err?.code ??
    err?.status ??
    err?.errorCode ??
    err?.data?.code ??
    ""
  ).toString();

  // Apple-specific cancellation handling comes first to avoid being swallowed by generic cancel detection
  if (
    /^1001$/.test(codeStr) ||
    /ASAuthorizationError|AuthorizationError.*1001/.test(msg)
  ) {
    return "Sign in with Apple was cancelled.";
  }

  if (isSSOCancelError(err)) return "Sign-in cancelled.";

  if (NETWORK_RE.test(msg)) {
    return "Network issue. Please check your internet connection and try again.";
  }
  if (msg) {
    return msg;
  }

  return `Failed to sign in with ${provider}. Please try again.`;
};
